import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ServiceItem, TimeSlot } from "@/types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";

const FRENCH_PHONE_REGEX = /^0[1-9](?: ?\d{2}){4}$/;

// Secure validation schema to prevent XSS, injection attacks, and data corruption
const bookingFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(100, "Le nom ne peut pas dépasser 100 caractères")
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Le nom ne peut contenir que des lettres, espaces, apostrophes et tirets")
    .refine(
      (value) => {
        // On découpe sur les espaces (un ou plusieurs)
        const parts = value.split(/\s+/).filter(Boolean);
        // On exige au moins 2 morceaux => "Bastien R", "Jean Claude", "Jean-Claude Dupont", etc.
        return parts.length >= 2;
      },
      {
        message: "Merci d’indiquer au moins un prénom et un nom.",
      },
    ),
  email: z
    .string()
    .trim()
    .email("Adresse email invalide")
    .max(255, "L'email ne peut pas dépasser 255 caractères")
    .toLowerCase(),
  phone: z.string().trim().regex(FRENCH_PHONE_REGEX, "Numéro de téléphone français invalide (ex. 06 12 34 56 78)"),
  notes: z.string().max(1000, "Les notes ne peuvent pas dépasser 1000 caractères").optional().default(""),
});

type BookingFormData = z.infer<typeof bookingFormSchema>;

interface ClientInfo {
  name: string;
  email: string;
  phone: string;
  notes: string;
}

interface ConfirmationFormProps {
  selectedServices: ServiceItem[];
  selectedDate: Date;
  selectedTimeSlot: TimeSlot | null;
  clientInfo: ClientInfo;
  onClientInfoChange: (info: ClientInfo) => void;
}

const formatFrenchPhone = (value: string): string => {
  // 1. On enlève tout ce qui n'est pas un chiffre
  const digitsOnly = value.replace(/\D/g, "").slice(0, 10); // max 10 chiffres

  // 2. On groupe par 2
  const chunks: string[] = [];
  for (let i = 0; i < digitsOnly.length; i += 2) {
    chunks.push(digitsOnly.slice(i, i + 2));
  }

  // 3. On rejoint avec des espaces : "06 12 34 56 78"
  return chunks.join(" ");
};

const formatPriceWithCurrency = (price: string) => {
  const priceMatch = price.match(/(\d+)[,.]?(\d*)/);
  if (!priceMatch) {
    return price; // au cas où le format serait inattendu
  }

  const numericValue = parseFloat(`${priceMatch[1]}.${priceMatch[2] || 0}`);

  // Si tu veux toujours 2 décimales :
  return `${numericValue.toFixed(2)}€`;

  // Si tu préfères sans décimales inutiles :
  // return `${numericValue.toString().replace('.', ',')}€`;
};

const ConfirmationForm = ({
  selectedServices,
  selectedDate,
  selectedTimeSlot,
  clientInfo,
  onClientInfoChange,
}: ConfirmationFormProps) => {
  // Initialize form with react-hook-form and Zod validation
  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      name: clientInfo.name,
      email: clientInfo.email,
      phone: clientInfo.phone,
      notes: clientInfo.notes || "",
    },
    mode: "onChange", // Validate on change for better UX
  });

  // Sync form values with parent component
  useEffect(() => {
    const subscription = form.watch((value) => {
      onClientInfoChange({
        name: value.name || "",
        email: value.email || "",
        phone: value.phone || "",
        notes: value.notes || "",
      });
    });
    return () => subscription.unsubscribe();
  }, [form, onClientInfoChange]);

  // Calculate total price
  const calculateTotalPrice = () => {
    return selectedServices.reduce((total, service) => {
      const priceMatch = service.price.match(/(\d+)[,.]?(\d*)/);
      const price = priceMatch ? parseFloat(`${priceMatch[1]}.${priceMatch[2] || 0}`) : 0;
      return total + price;
    }, 0);
  };

  return (
    <div className="space-y-5 pb-4">
      {/* Selected Services Summary */}
      <div>
        <h3 className="text-base font-medium mb-2">Récapitulatif des services</h3>
        <div className="bg-bbz-beige/10 rounded-md p-3">
          <ul className="space-y-2">
            {selectedServices.map((service) => (
              <li key={service.id} className="flex justify-between text-sm">
                <span className="flex-1 pr-3">{service.name}</span>
                <span className="font-medium whitespace-nowrap">{formatPriceWithCurrency(service.price)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-bbz-green/30 mt-2 pt-2 flex justify-between">
            <span className="font-medium">Total</span>
            <span className="font-medium">{calculateTotalPrice().toFixed(2)}€</span>
          </div>
        </div>
      </div>

      {/* Date and Time */}
      <div>
        <h3 className="text-base font-medium mb-2">Date et horaire</h3>
        <div className="bg-bbz-beige/10 rounded-md p-3">
          <div className="flex flex-col text-sm">
            <span>{format(selectedDate, "EEEE dd MMMM yyyy", { locale: fr })}</span>
            <span className="font-medium">
              {selectedTimeSlot ? `${selectedTimeSlot.startTime}` : "Aucun horaire sélectionné"}
            </span>
          </div>
        </div>
      </div>

      {/* Client Information Form with Validation */}
      <div>
        <h3 className="text-base font-medium mb-2">Vos informations</h3>
        <Form {...form}>
          <form className="space-y-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Nom complet *</FormLabel>
                  <FormControl>
                    <Input placeholder="Votre nom et prénom" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="votre.email@example.com" {...field} />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Une confirmation vous sera envoyée par email. Si vous êtes déjà venue, utilisez la même adresse pour que je retrouve votre fiche.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Téléphone *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="06 12 34 56 78"
                      inputMode="tel"
                      autoComplete="tel"
                      maxLength={14} // 00 00 00 00 00
                      pattern="\d{2}( \d{2}){4}"
                      onChange={(e) => {
                        const formatted = formatFrenchPhone(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Numéro français à 10 chiffres (ex. 06 12 34 56 78)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Notes ou demandes particulières</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Informations complémentaires, besoins spécifiques..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ConfirmationForm;
export { bookingFormSchema };
export type { BookingFormData };
